import {
  PROJECT_VERSION,
  cloneConfig,
  defaultEngineConfig,
  defaultRenderSettings,
  type EngineConfig,
  type Point,
  type Project,
  type RenderSettings,
  type SourceMetadata,
} from '@/types/engine';
import type { AppState } from '@/state/appState';
import { sequenceView } from '@/state/appState';

export class ProjectParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProjectParseError';
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function mergeDefaults<T extends object>(defaults: T, value: unknown): T {
  if (!isObject(value)) return defaults;
  const out: Record<string, unknown> = { ...(defaults as Record<string, unknown>) };
  for (const [k, v] of Object.entries(value)) {
    const d = (defaults as Record<string, unknown>)[k];
    if (isObject(d) && isObject(v) && !('type' in d)) out[k] = mergeDefaults(d as object, v);
    else if (v !== undefined) out[k] = v;
  }
  return out as T;
}

export function coreVersion(): string {
  return '0.1.0';
}

export function buildProject(state: AppState, wasmVersion: string): Project {
  const { solution, config, render, source, target } = state;
  const seq = sequenceView(solution);
  const pairs: [number, number][] = [];
  for (let i = 0; i < seq.length; i += 2) pairs.push([seq[i], seq[i + 1]]);
  const pins: Point[] = [];
  for (let i = 0; i < solution.pins.length; i += 2) pins.push([solution.pins[i], solution.pins[i + 1]]);
  const meta: SourceMetadata = {
    kind: source.kind ?? 'unknown',
    name: source.name,
    width: source.width,
    height: source.height,
  };
  return {
    version: PROJECT_VERSION,
    coreVersion: wasmVersion || coreVersion(),
    source: meta,
    target: { width: target.width, height: target.height },
    config: cloneConfig(config),
    pinCoordinates: pins,
    sequence: pairs,
    render: cloneConfig(render),
  };
}

export function serializeProject(project: Project): string {
  return JSON.stringify(project, null, 2);
}

export function parseProject(text: string): Project {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (err) {
    throw new ProjectParseError(`The file is not valid JSON (${err instanceof Error ? err.message : String(err)}).`);
  }
  if (!isObject(raw)) throw new ProjectParseError('A project file must contain a JSON object.');
  const version = raw.version;
  if (typeof version !== 'number' || !Number.isInteger(version)) throw new ProjectParseError('The project has no numeric "version" field.');
  if (version < 1 || version > PROJECT_VERSION) {
    throw new ProjectParseError(`Project version ${version} is not supported by this build (supports up to ${PROJECT_VERSION}).`);
  }
  const target = raw.target;
  if (!isObject(target) || typeof target.width !== 'number' || typeof target.height !== 'number' || target.width <= 0 || target.height <= 0) {
    throw new ProjectParseError('The project has no valid target dimensions.');
  }
  const pins = Array.isArray(raw.pinCoordinates) ? raw.pinCoordinates : [];
  const pinCoordinates: Point[] = pins.map((p, i) => {
    if (!Array.isArray(p) || p.length < 2 || typeof p[0] !== 'number' || typeof p[1] !== 'number' || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) {
      throw new ProjectParseError(`Pin ${i} is not a finite [x, y] pair.`);
    }
    return [p[0], p[1]];
  });
  const seqRaw = Array.isArray(raw.sequence) ? raw.sequence : [];
  const sequence: [number, number][] = seqRaw.map((s, i) => {
    if (!Array.isArray(s) || s.length < 2 || !Number.isInteger(s[0]) || !Number.isInteger(s[1]) || s[0] < 0 || s[1] < 0 || s[0] > 65535 || s[1] > 65535) {
      throw new ProjectParseError(`String ${i} is not a pair of pin ids.`);
    }
    if (pinCoordinates.length > 0 && (s[0] >= pinCoordinates.length || s[1] >= pinCoordinates.length)) {
      throw new ProjectParseError(`String ${i} references pin ${Math.max(s[0], s[1])} but the project stores ${pinCoordinates.length} pins.`);
    }
    return [s[0], s[1]];
  });
  const config = mergeDefaults<EngineConfig>(defaultEngineConfig(), raw.config);
  const render = mergeDefaults<RenderSettings>(defaultRenderSettings(), raw.render);
  const sourceRaw = isObject(raw.source) ? raw.source : {};
  const kind = sourceRaw.kind;
  const source: SourceMetadata = {
    kind: kind === 'image' || kind === 'synthetic' ? kind : 'unknown',
    name: typeof sourceRaw.name === 'string' ? sourceRaw.name : '',
    width: typeof sourceRaw.width === 'number' ? sourceRaw.width : 0,
    height: typeof sourceRaw.height === 'number' ? sourceRaw.height : 0,
  };
  return {
    version: PROJECT_VERSION,
    coreVersion: typeof raw.coreVersion === 'string' ? raw.coreVersion : '',
    source,
    target: { width: target.width, height: target.height },
    config,
    pinCoordinates,
    sequence,
    render,
  };
}
